import { Skeleton } from '@mantine/core'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AvgOutreachAttempts {
  completedAvg: number
  noResponseAvg: number
  totalAvg: number
  completedCount: number
  noResponseCount: number
  totalCount: number
}

// ---------------------------------------------------------------------------
// Stat row — full-width row with accent bar, large number, and context
// ---------------------------------------------------------------------------

interface StatRowProps {
  label: string
  avg: number
  count: number
  color: string
}

const StatRow = ({ label, avg, count, color }: StatRowProps) => (
  <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-4 py-3">
    <div
      className="h-10 w-1 flex-shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
    <div className="flex flex-1 items-center justify-between">
      <div>
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <p className="text-[11px] text-gray-400">
          {count} verification{count !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-semibold text-gray-900">{avg}</span>
        <span className="text-xs text-gray-400">avg</span>
      </div>
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface VoEAvgAttemptsChartProps {
  data: AvgOutreachAttempts | null | undefined
  loading?: boolean
}

export const VoEAvgAttemptsChart = ({
  data,
  loading = false,
}: VoEAvgAttemptsChartProps) => {
  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton height={58} radius="lg" />
        <Skeleton height={58} radius="lg" />
        <Skeleton height={58} radius="lg" />
      </div>
    )
  }

  if (!data || data.totalCount === 0) {
    return (
      <div className="flex h-[160px] items-center justify-center text-sm text-gray-400">
        No data available
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <StatRow
        label="With Response"
        avg={data.completedAvg}
        count={data.completedCount}
        color="#47CD89"
      />
      <StatRow
        label="Without Response"
        avg={data.noResponseAvg}
        count={data.noResponseCount}
        color="#FDA29B"
      />
      <StatRow
        label="Total"
        avg={data.totalAvg}
        count={data.totalCount}
        color="#344054"
      />
    </div>
  )
}
